import { useAppStore } from "../state/app-store";
import { useShallow } from "zustand/react/shallow";
import { easyblocksConfig } from "../utils/easyblocks-config/index";
import { waitForPendingDocumentWrites } from "../api/adapters/custom-backend";

type EditorControlsProps = {
  onSaveComplete?: () => void;
  onDiscardComplete?: () => void;
};

export function EditorControls({ onSaveComplete, onDiscardComplete }: EditorControlsProps) {
  const {
    currentDocument,
    baselineDocument,
    isSaving,
    saveDelayMs,
    documentId,
    runtimeMode,
    setIsSaving,
    setLastSaveError,
  } = useAppStore(useShallow((state) => ({
    currentDocument: state.currentDocument,
    baselineDocument: state.baselineDocument,
    isSaving: state.isSaving,
    saveDelayMs: state.saveDelayMs,
    documentId: state.documentId,
    runtimeMode: state.runtimeMode,
    setIsSaving: state.setIsSaving,
    setLastSaveError: state.setLastSaveError,
  })));

  // Detect dev mode using the same logic as config.ts
  const isDev = Boolean((import.meta as any)?.env?.DEV || import.meta?.url?.includes("localhost"));
  
  const isEditMode = runtimeMode === "editor";

  // In dev mode, when NOT in editor, show "Edit mode" button instead
  if (isDev && !isEditMode) {
    const nextUrl = new URL(window.location.href);
    nextUrl.search = "";
    nextUrl.searchParams.set("mode", "editor");
    nextUrl.searchParams.set("readOnly", "false");
    nextUrl.searchParams.set("rootComponent", "RootLayoutSections");
    if (documentId) {
      nextUrl.searchParams.set("document", documentId);
    }

    return (
      <div className="easyblocks-editor-controls d-flex flex-wrap gap-2 justify-content-center p-3 bg-body-tertiary border-top border-bottom">
        <a href={nextUrl.toString()} className="btn btn-success px-4 py-2">
          Edit mode
        </a>
      </div>
    );
  }

  const handleSave = async () => {
    if (isSaving || !currentDocument) return;

    try {
      setIsSaving(true);
      setLastSaveError(null);
      console.log("[EditorControls] Save clicked, committing pending edits...");

      // Step 1: Force-blur the active element so any in-progress field
      // commits its value into the form state. Selects, color pickers, and
      // rich-text editors typically commit on blur — without this, a user who
      // edits and clicks Save quickly can lose the change.
      if (typeof document !== "undefined" && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      // Yield long enough for onBlur handlers and the form library's internal
      // propagation to settle.
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Step 2: Drain any in-flight or queued auto-save writes so the
      // store's currentDocument.version reflects the latest server state
      // before we submit the manual checkpoint.
      await waitForPendingDocumentWrites({ mode: "drain", timeoutMs: 5000 });

      // Step 3: Force a manual checkpoint write.
      // Autosaves keep the page body current, but only this path records a revision.
      const editorContextWindow = window as typeof window & {
        editorWindowAPI?: { editorContext?: { form?: { values?: unknown } } };
      };
      const formValues = editorContextWindow.editorWindowAPI?.editorContext?.form?.values;
      const latestDoc = useAppStore.getState().currentDocument;
      if (latestDoc) {
        const backend = (easyblocksConfig as any)?.backend;
        const updateDocument = backend?.documents?.update;
        const nextEntry = formValues ?? latestDoc.entry;

        if (typeof updateDocument !== "function") {
          throw new Error("Backend does not support documents.update");
        }

        try {
          await updateDocument({
            id: latestDoc.id,
            version: latestDoc.version,
            entry: nextEntry,
            record_revision: true,
          });
        } catch (e) {
          const status = (e as { status?: number } | null)?.status;
          if (status === 409) {
            // Auto-save raced us and won. Retry once with the latest server state
            // so the manual save still leaves a revision checkpoint.
            const refreshedDoc = useAppStore.getState().currentDocument;
            if (refreshedDoc) {
              await updateDocument({
                id: refreshedDoc.id,
                version: refreshedDoc.version,
                entry: nextEntry,
                record_revision: true,
              });
            }
          } else {
            throw e;
          }
        }
      }

      // Step 4: Drain again, in case our manual write or a late auto-save is
      // still in flight.
      await waitForPendingDocumentWrites({ mode: "drain", timeoutMs: 5000 });

      // Note: saveDelayMs is no longer used here. The drain + explicit-write
      // path is reliable regardless of how long the auto-save debounce is.
      void saveDelayMs;

      console.log("[EditorControls] Save complete");

      if (onSaveComplete) {
        onSaveComplete();
      } else {
        // Default: reload the page to render mode
        const url = new URL(window.location.href);
        url.search = "";
        window.location.assign(url.toString());
      }
    } catch (error) {
      console.error("[EditorControls] Save failed", error);
      setLastSaveError(error instanceof Error ? error : new Error(String(error)));
      setIsSaving(false);
    }
  };

  const handleDiscard = async () => {
    if (isSaving) return;

    const confirmed = window.confirm(
      "Are you sure you want to discard all changes? This will revert to the document state when you started editing."
    );
    if (!confirmed) return;

    if (!baselineDocument) {
      console.warn("[EditorControls] No baseline document to restore");
      // Just exit without restoring
      if (onDiscardComplete) {
        onDiscardComplete();
      } else {
        const url = new URL(window.location.href);
        url.search = "";
        window.location.assign(url.toString());
      }
      return;
    }

    try {
      setIsSaving(true);
      setLastSaveError(null);
      console.log("[EditorControls] Discard clicked, restoring baseline...");

      const backend = (easyblocksConfig as any)?.backend;
      const updateDocument = backend?.documents?.update;
      if (typeof updateDocument !== "function") {
        throw new Error("Backend does not support documents.update");
      }

      // Restore the baseline document
      await updateDocument(baselineDocument);
      console.log("[EditorControls] Baseline restored");

      if (onDiscardComplete) {
        onDiscardComplete();
      } else {
        // Reload to render mode
        const url = new URL(window.location.href);
        url.search = "";
        window.location.assign(url.toString());
      }
    } catch (error) {
      console.error("[EditorControls] Discard failed", error);
      setLastSaveError(error instanceof Error ? error : new Error(String(error)));
      setIsSaving(false);
      alert("Failed to discard changes. Please try again.");
    }
  };

  return (
    <div className="easyblocks-editor-controls d-flex flex-wrap gap-2 justify-content-center p-3 bg-body-tertiary border-top border-bottom">
      <div className="editor-controls ms-auto d-flex gap-2 justify-content-end align-items-center">
        <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={handleDiscard}
            disabled={isSaving}
        >
          {isSaving ? "Processing..." : "Discard changes"}
        </button>
        <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={handleSave}
            disabled={isSaving || !currentDocument}
        >
          {isSaving ? "Saving..." : "Save changes"}
        </button>
      </div>
    </div>
  );
}
